'use client'

import { ButtonGhost } from '@fukku/button/Ghost'
import { ButtonPrimary } from '@fukku/button/Primary'
import { IconLocationL } from 'icons/components/IconLocationL'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { StoreSchedule } from 'maps/components/commons/StoreSchedule/StoreSchedule'
import { useExpandedStore } from 'maps/hooks/useExpandedStore/useExpandedStore'
import type { Store } from 'maps/types'
import {
	sendSearchStoreMapEvent,
	sendSearchStoreSelectEvent,
} from 'maps/utils/analytics/events'
import { useEffect } from 'react'
import { cx } from 'utils/cx'

import styles from './StoreInfo.module.scss'

interface StoreInfoProps {
	readonly store: Store
	readonly showPhone?: boolean
	readonly showDisclaimer?: boolean
	readonly onConfirm?: (store: Store) => void
	readonly onViewMap?: () => void
	readonly isExpanded?: boolean
	readonly ariaIds: {
		descriptionId: string
		extendedInfoId: string
	}
}

export function StoreInfo({
	store,
	showPhone,
	showDisclaimer,
	onConfirm,
	onViewMap,
	isExpanded,
	ariaIds,
}: StoreInfoProps) {
	const { t } = useLabels()
	const { id } = store
	const { setExpanded } = useExpandedStore()

	const onClickConfirm = (): void => {
		if (onConfirm) {
			sendSearchStoreSelectEvent()
			onConfirm(store)
		}
	}

	const handleOnViewMap = () => {
		sendSearchStoreMapEvent()

		if (onViewMap) {
			onViewMap()
		}
	}

	useEffect(() => {
		if (isExpanded && !store.details) {
			setExpanded(store)
		}
	}, [isExpanded])

	return (
		<div className={styles.info} id={ariaIds.extendedInfoId}>
			<div className={cx(styles.wrapper, !onViewMap && styles.map)}>
				<StoreSchedule
					store={store}
					showDisclaimer={showDisclaimer}
					showPhone={showPhone}
				/>
				{onViewMap && (
					<ButtonGhost
						className={styles.viewMap}
						onClick={handleOnViewMap}
						data-testid='maps.address.store.map.button'
						aria-describedby={`store-${id}-description`}
					>
						<IconLocationL />
						{t('storeLocator.card.map.link')}
					</ButtonGhost>
				)}
			</div>
			{onConfirm && (
				<ButtonPrimary
					aria-describedby={`store-${id}-description`}
					onClick={onClickConfirm}
					data-testid='maps.address.store.select.button'
					className={styles.cta}
					disabled={store.details?.isCorner}
				>
					{t('checkout.shipping.store.card.select.button')}
				</ButtonPrimary>
			)}
		</div>
	)
}
